// Transcript store — what the chat panel renders.
//
// Holds the agent-loop conversation history (Message[]) plus streaming
// scaffolding (current partial assistant text, streaming flag, abort
// controller for the stop button, last error).
//
// Scope: bound to one project at a time. When the user switches projects
// we abort any in-flight stream and reset state. Per-project transcript
// PERSISTENCE is deferred — for first cut, history lives in memory only
// and is lost on reload. The cost is small (each project starts fresh on
// a hard reload) and the simpler invariant ("transcript reflects the
// current session") is easier to reason about while we iterate on the
// chat panel UX. Persistence will be added in a later phase by saving
// `messages` to IDB keyed by project name on every assistant_text_complete /
// turn_complete event.
//
// Why a separate store from projects.ts: transcripts mutate ~30× per
// second during streaming (one set() per text_delta event); we don't
// want React to re-render the project picker that frequently, and we
// don't want to bloat the projects-index IDB blob with 100KB transcripts.

import { create } from 'zustand';

import type { Message } from '../core/agent/types';

interface TranscriptStore {
  /** The project this store is currently bound to. */
  projectName: string | null;

  /** Persistent conversation history — what the agent loop reads/writes. */
  messages: Message[];

  /** Live partial assistant text, flushed into a real assistant message
   * when streaming completes (or a tool_use interrupts the text). */
  streamingText: string;

  /** True while the agent loop is mid-turn. */
  streaming: boolean;

  /** Abort controller for the in-flight turn (powers the stop button). */
  controller: AbortController | null;

  /** Last error message surfaced from the agent loop. Cleared on next send. */
  errorMessage: string | null;

  /**
   * Switch to a project. Aborts any in-flight stream and resets state.
   * Pass the same project name twice = no-op.
   */
  bindProject: (name: string) => void;

  /** Push any agent-loop Message (user / assistant / tool_result). */
  appendMessage: (msg: Message) => void;

  /** Replace the entire messages array. Used by the orchestrator after
   * the agent loop mutates its internal history, so React sees a fresh
   * array and re-renders. */
  setMessages: (messages: Message[]) => void;

  /** Live-stream a chunk of assistant text into the current partial. */
  appendStreamingDelta: (delta: string) => void;

  /**
   * Move whatever's in streamingText out as a final assistant text block
   * appended to the most recent assistant message (or as a new assistant
   * message if there is none yet). Resets streamingText to ''.
   *
   * Called on assistant_text_complete (mid-turn, before a tool_use) and
   * implicitly via flushStreamingToMessages on usage / turn_complete.
   */
  flushStreamingText: () => void;

  /** Mark stream begin/end + bind controller. */
  setStreaming: (
    on: boolean,
    controller?: AbortController | null,
  ) => void;

  /** Surface an error inline in the transcript. */
  setError: (msg: string | null) => void;

  /** Cancel the in-flight turn (wired to the stop button). */
  abort: () => void;

  /** Wipe transcript + abort. Used by "新對話" UX. */
  clear: () => void;
}

const INITIAL: Pick<
  TranscriptStore,
  'messages' | 'streamingText' | 'streaming' | 'controller' | 'errorMessage'
> = {
  messages: [],
  streamingText: '',
  streaming: false,
  controller: null,
  errorMessage: null,
};

export const useTranscript = create<TranscriptStore>((set, get) => ({
  projectName: null,
  ...INITIAL,

  bindProject: (name) => {
    if (get().projectName === name) return;
    // Abort any stream tied to the previous project before swapping state.
    get().controller?.abort();
    set({ projectName: name, ...INITIAL });
  },

  appendMessage: (msg) => {
    set({ messages: [...get().messages, msg] });
  },

  setMessages: (messages) => {
    set({ messages });
  },

  appendStreamingDelta: (delta) => {
    set({ streamingText: get().streamingText + delta });
  },

  flushStreamingText: () => {
    const text = get().streamingText;
    if (!text) return;
    // Append as its own assistant message with a single text content block.
    // The agent loop pushes a unified AssistantMessage at message_complete
    // (with text + any tool_use blocks), but we maintain a separate
    // "live text" channel so the chat panel can render mid-stream without
    // reaching into the loop's internal accumulator.
    //
    // To avoid duplicating text in history, the loop wiring (Phase 2C)
    // explicitly does NOT call this for assistant_text_complete events —
    // it lets the loop's own AssistantMessage push handle persistence.
    // This method exists for safety: if a stream ends abnormally we want
    // any partial text already shown to land in messages too.
    const messages = get().messages;
    set({
      messages: [
        ...messages,
        { role: 'assistant', content: [{ type: 'text', text }] },
      ],
      streamingText: '',
    });
  },

  setStreaming: (on, controller) => {
    set({
      streaming: on,
      controller: controller === undefined ? get().controller : controller,
    });
  },

  setError: (msg) => set({ errorMessage: msg }),

  abort: () => {
    const ctrl = get().controller;
    ctrl?.abort();
    set({ streaming: false });
  },

  clear: () => {
    get().controller?.abort();
    set({ ...INITIAL });
  },
}));
